// ── BCHNostr — Creator Goal data for embeddable widgets ───────────
//
// A login-free hook that loads a creator's active Support Goal and its raised
// total for the standalone embed page. It reuses the EXACT same data sources
// as the Creator Support page — the creator's kind 30078 `bch-creator-goal`
// event, their on-chain BCH balance, and kind 9735 `bch-goal-pledge` receipts
// — so an embedded widget always shows the same numbers as the real goal, and
// updates automatically as pledges and on-chain donations arrive.
//
// "Raised" mirrors CreatorSupportPage: it sums the goal pledges scoped to the
// current goal (by the stable `goal_created` id), and also considers the
// creator's on-chain balance once at least one pledge exists — so a wallet
// that merely holds BCH doesn't fake progress on a brand-new goal.

import { useEffect, useMemo, useState } from 'react';
import { useNostrQuery } from './useNostrQuery';
import { useBCHAddress } from './useBCHAddress';
import {
  GOAL_KIND, GOAL_TAG, GOAL_PLEDGE_KIND, GOAL_PLEDGE_TAG,
  goalProgress, usdToSatsMembership,
  type CreatorGoalData,
} from '../lib/creatorSupport';

export interface GoalEmbedData {
  loading: boolean;
  found: boolean;
  creatorPubkey: string;
  data: CreatorGoalData | null;
  raisedSats: number;
  targetSats: number;
  targetUsd: number;
  pct: number;
  backerCount: number;
}

const ON_CHAIN_REFRESH_MS = 60_000; // widget refreshes on-chain every 60s

export function useGoalEmbedData(
  creatorPubkey: string | undefined,
  bchPrice: number | null,
): GoalEmbedData {
  // 1. The creator's active goal (kind 30078, one replaceable event).
  const { events: goalEvents } = useNostrQuery({
    filters: creatorPubkey
      ? [{ kinds: [GOAL_KIND], authors: [creatorPubkey], '#t': [GOAL_TAG], limit: 1 }]
      : [],
    enabled: !!creatorPubkey,
    allRelays: true,
  });

  const goal = useMemo(() => {
    if (!goalEvents.length) return null;
    const ev = [...goalEvents].sort((a, b) => b.created_at - a.created_at)[0];
    try {
      const data: CreatorGoalData = JSON.parse(ev.content);
      return { event: ev, data };
    } catch {
      return null;
    }
  }, [goalEvents]);

  // 2. Pledges to this creator's goals (kind 9735, bch-goal-pledge), scoped to
  //    the current goal by the stable `goal_created` id — exactly like the
  //    Creator Support page's useGoalPledges.
  const { events: pledgeEvents } = useNostrQuery({
    filters: creatorPubkey
      ? [{ kinds: [GOAL_PLEDGE_KIND], '#p': [creatorPubkey], '#t': [GOAL_PLEDGE_TAG], limit: 500 }]
      : [],
    enabled: !!creatorPubkey,
    allRelays: true,
  });

  const { nostrRaisedSats, backerCount } = useMemo(() => {
    const goalCreated = goal?.data.createdAt ? String(goal.data.createdAt) : '';
    const goalEventId = goal?.event.id;

    const scoped = pledgeEvents.filter((ev) => {
      const gc = ev.tags?.find((t: string[]) => t[0] === 'goal_created')?.[1];
      if (gc) return goalCreated ? gc === goalCreated : true;
      // Legacy pledge without a stable id — fall back to the current event id.
      if (goalEventId) {
        const eTag = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
        return eTag === goalEventId;
      }
      return false;
    });

    const byId = new Map<string, typeof scoped[number]>();
    for (const ev of scoped) byId.set(ev.id, ev);
    const pledges = [...byId.values()];

    const sum = pledges.reduce((s, ev) => {
      const amt = ev.tags?.find((t: string[]) => t[0] === 'amount')?.[1];
      return s + (amt ? parseInt(amt) || 0 : 0);
    }, 0);
    return { nostrRaisedSats: sum, backerCount: pledges.length };
  }, [pledgeEvents, goal]);

  // 3. On-chain balance of the creator's profile BCH address (ground truth).
  const { address: creatorBCHAddress } = useBCHAddress(creatorPubkey);
  const [onChainSats, setOnChainSats] = useState<number | null>(null);

  useEffect(() => {
    if (!creatorBCHAddress) return;
    const addr = creatorBCHAddress.replace(/^bitcoincash:/i, '');
    const url = `https://proxy.shakespeare.diy/?url=${encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${addr}`)}`;
    let cancelled = false;

    const fetchBalance = () => {
      fetch(url, { signal: AbortSignal.timeout(8000) })
        .then(r => r.json())
        .then(json => {
          if (cancelled) return;
          const confirmed = (json?.chain_stats?.funded_txo_sum ?? 0) - (json?.chain_stats?.spent_txo_sum ?? 0);
          const unconfirmed = (json?.mempool_stats?.funded_txo_sum ?? 0) - (json?.mempool_stats?.spent_txo_sum ?? 0);
          setOnChainSats(Math.max(0, confirmed + unconfirmed));
        })
        .catch(() => { /* ignore — widget falls back to Nostr pledge sum */ });
    };

    fetchBalance();
    const timer = setInterval(fetchBalance, ON_CHAIN_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [creatorBCHAddress]);

  // Give the relay query a bounded window. After it elapses we stop showing
  // the loading state and fall back to "no active goal" rather than spinning
  // forever (e.g. slow relays, or a creator who genuinely has no goal).
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    setTimedOut(false);
    if (!creatorPubkey) return;
    const t = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [creatorPubkey]);

  const data = goal?.data ?? null;
  const targetUsd = data?.targetUsd ?? 0;
  const targetSats = usdToSatsMembership(targetUsd, bchPrice);

  // Raised: use on-chain as ground truth only once real pledges exist, so a
  // creator's existing wallet balance never fakes progress on a fresh goal.
  const raisedSats = useMemo(() => {
    const hasPledges = nostrRaisedSats > 0;
    return hasPledges && onChainSats !== null
      ? Math.max(onChainSats, nostrRaisedSats)
      : nostrRaisedSats;
  }, [nostrRaisedSats, onChainSats]);

  const pct = data ? goalProgress(raisedSats, targetUsd, bchPrice) : 0;

  const loading = !!creatorPubkey && !goal && !timedOut;

  return {
    loading,
    found: !!goal && !!data,
    creatorPubkey: creatorPubkey || '',
    data,
    raisedSats,
    targetSats,
    targetUsd,
    pct,
    backerCount,
  };
}
